import { Block, PendingActions } from "@mui/icons-material";
import { Chip, ChipProps, Stack } from "@mui/material";
import { ColDef, GetRowIdParams, ICellRendererParams, IServerSideDatasource, ValueFormatterParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { AgGridCustomToolbarProps } from "../../../../../util/component/AgGridCustomToolbar";
import { getRules, getRulesCount } from "../../../api/services";
import { ISortBy } from "../../../api/types";
import { IRule, RuleStatus, RuleType } from "../../../types";
import { rulesFilterState } from "../recoil";
import CellActions from "./cell-actions/CellActions";
import CellParameters from "./cell-parameters/CellParameters";
import CellPickupLocations from "./cell-pickup-locations/CellPickupLocations";
import HeaderActions from "./header-actions/HeaderActions";
import ToolbarActions from "./toolbar/ToolbarActions";
import CustomLoader from "../../../../../util/component/CustomLoader";

const TableRules = () => {

    const { t } = useTranslation('booking');

    const rulesFilter = useRecoilValue(rulesFilterState);

    const rulesListMutation = useMutation(getRules);
    const rulesCountMutation = useMutation(getRulesCount);

    const columnDefs = useMemo<ColDef[]>(() => [
        {
            field: '_select',
            headerName: '',
            checkboxSelection: true,
            minWidth: 60,
            maxWidth: 60,
            filter: false,
            resizable: false,
            pinned: 'left',
            suppressNavigable: true,
            suppressMovable: true
        },
        {
            field: 'status',
            headerName: t('label.status'),
            pinned: 'left',
            resizable: false,
            maxWidth: 130,
            minWidth: 130,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                let label = undefined;
                let color: ChipProps['color'] | undefined = undefined;
                switch (params.value) {
                    case RuleStatus.Active:
                        label = t('label.active');
                        color = 'success';
                        break;
                    case RuleStatus.NotActive:
                        label = t('label.notActive');
                        color = 'error';
                        break;
                    case RuleStatus.OnHold:
                        label = t('label.onHold');
                        color = 'warning';
                        break;
                    default:
                        return undefined;
                }
                return (
                    <Chip label={label} color={color} />
                );
            },
            suppressMovable: true
        },
        {
            field: 'type',
            headerName: t('label.type'),
            pinned: 'left',
            resizable: false,
            minWidth: 100,
            suppressMovable: true,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                let icon: React.ReactNode | undefined = undefined;
                switch (params.value) {
                    case RuleType.Pending:
                        icon = <PendingActions titleAccess={t('label.pending')} color="info" />;
                        break;
                    case RuleType.StopSells:
                        icon = <Block titleAccess={t('label.stopSell')} color="error" />;
                        break;
                }
                return (
                    <Stack direction="row" alignItems="center">
                        {icon}
                    </Stack>
                )
            },
        },
        {
            field: 'name',
            headerName: t('label.name'),
            sortable: true
        },
        {
            field: 'pickupLocations',
            headerName: t('label.pickupLocations'),
            cellRenderer: CellPickupLocations
        },
        {
            field: 'pickupDateFrom',
            headerName: t('label.pickupDateFrom'),
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                const {
                    pickupDateTimeFrom,
                    excludedPickupDateTimeFrom
                } = params.data ?? {};
                if (!pickupDateTimeFrom)
                    return '-';
                return dayjs(pickupDateTimeFrom).format('DD/MM/YYYY HH:mm') + (excludedPickupDateTimeFrom ? ` (${t('label.excluded')})` : '');
            }
        },
        {
            field: 'pickupDateTo',
            headerName: t('label.pickupDateTo'),
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                const {
                    pickupDateTimeTo,
                    excludedPickupDateTimeTo
                } = params.data ?? {};
                if (!pickupDateTimeTo)
                    return '-';
                return dayjs(pickupDateTimeTo).format('DD/MM/YYYY HH:mm') + (excludedPickupDateTimeTo ? ` (${t('label.excluded')})` : '');
            }
        },
        {
            field: 'includingDateFrom',
            headerName: t('label.includingDateFrom'),
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                const {
                    includingDateTimeFrom,
                    excludedIncludingDateTimeFrom
                } = params.data ?? {};
                if (!includingDateTimeFrom)
                    return '-';
                return dayjs(includingDateTimeFrom).format('DD/MM/YYYY HH:mm') + (excludedIncludingDateTimeFrom ? ` (${t('label.excluded')})` : '');
            }
        },
        {
            field: 'includingDateTo',
            headerName: t('label.includingDateTo'),
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                const {
                    includingDateTimeTo,
                    excludedIncludingDateTimeTo
                } = params.data ?? {};
                if (!includingDateTimeTo)
                    return '-';
                return dayjs(includingDateTimeTo).format('DD/MM/YYYY HH:mm') + (excludedIncludingDateTimeTo ? ` (${t('label.excluded')})` : '');
            }
        },
        {
            field: 'acrissList',
            headerName: t('label.acrissList'),
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                const {
                    acrissList,
                    excludedAcrissList
                } = params.data ?? {};
                if (!acrissList || acrissList.length === 0)
                    return '-';
                return acrissList.join(', ') + (excludedAcrissList ? ` (${t('label.excluded')})` : '');
            }
        },
        {
            field: 'sippList',
            headerName: t('label.sippList'),
            cellRenderer: (params: ICellRendererParams<IRule>) => {
                const {
                    sippList,
                    excludedSippList
                } = params.data ?? {};
                if (!sippList || sippList.length === 0)
                    return '-';
                return sippList.join(', ') + (excludedSippList ? ` (${t('label.excluded')})` : '');
            }
        },
        {
            field: 'creationDateTime',
            headerName: t('label.creationDate'),
            cellRenderer: (params: ICellRendererParams) => params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : undefined,
            sortable: true
        },
        {
            field: 'user',
            headerName: t('label.user'),
            valueFormatter: (params: ValueFormatterParams<IRule>) => params.data?.creationOperator.username ?? '-',
            sortable: true
        },
        {
            field: 'parameters',
            headerName: t('label.parameters'),
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: CellParameters
        },
        {
            field: '_actions',
            headerName: '',
            resizable: false,
            pinned: 'right',
            suppressNavigable: true,
            suppressMovable: true,
            minWidth: 110,
            maxWidth: 110,
            headerComponent: HeaderActions,
            cellRenderer: CellActions,
        },
    ], [t]);
    const defaultColDef = useMemo<ColDef>(() => ({
        lockPinned: true,
        resizable: true,
        unSortIcon: true
    }), []);
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                params.api.deselectAll();
                const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                    field: params.request.sortModel[0].colId,
                    order: params.request.sortModel[0].sort
                } : undefined;
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                let rulesCount: number;

                rulesCountMutation.mutateAsync({
                    filters: rulesFilter
                }).then((response: {
                    counter: {
                        total: number
                    }
                }) => {
                    rulesCount = response.counter.total;

                    rulesListMutation.mutateAsync({
                        filter: rulesFilter,
                        sortBy,
                        start,
                        limit
                    }).then((response: {
                        rows: IRule[]
                    }) => {
                        params.success({
                            rowData: response.rows,
                            rowCount: rulesCount ?? 0
                        });

                        if (rulesCount === 0) {
                            params.api.showNoRowsOverlay();
                        }
                    }).catch((err: AxiosError) => {
                        params.fail();
                        params.api.hideOverlay();
                    });
                }).catch((err: AxiosError) => {
                    params.fail();
                    params.api.hideOverlay();
                });
            } catch (err) {
                params.fail();
            }
        },
    }), [rulesFilter]);
    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderActions: ToolbarActions
    }), []);

    const getRowId = useCallback((params: GetRowIdParams<IRule>) => {
        return String(params.data.id);
    }, []);

    return (
        <>
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowModelType="serverSide"
                serverSideInfiniteScroll
                serverSideDatasource={serverSideDatasource}
                getRowId={getRowId}
                toolbar={toolbar}
                paginationPageSize={25}
                cacheBlockSize={25}
                rowSelection='multiple'
                suppressMultiSort
                suppressRowClickSelection
                suppressDragLeaveHidesColumns
            />
            <CustomLoader
                loading={rulesListMutation.isLoading ||
                rulesCountMutation.isLoading}
            />
        </>
    );
};

export default TableRules;
